<script setup lang="ts">
import { computed, inject, reactive, ref, watch } from 'vue'
import { formsContextKey } from '../../forms-context'
import type {
  WebsiteForm,
  WebsiteFormCaptureState,
  WebsiteFormField,
  WebsiteFormSubmission,
} from '../../types'

const props = defineProps<{
  formId: string
  formCapture?: WebsiteFormCaptureState
  submitLabel?: string
}>()
const emit = defineEmits<{ submit: [submission: WebsiteFormSubmission] }>()

const context = inject(formsContextKey, null)
const form = computed<WebsiteForm | undefined>(() => context?.forms.value.get(props.formId))
const answers = reactive<Record<string, unknown>>({})
const honeypot = ref('')
const effectiveCapture = computed<WebsiteFormCaptureState>(() => props.formCapture ?? ({
  enabled: true,
  submitting: false,
  succeeded: false,
}))
const submitLabel = computed(() => props.submitLabel || form.value?.submitLabel || 'Submit')

for (const field of form.value?.fields ?? []) {
  if (field.defaultValue) {
    answers[field.key] = field.defaultValue
  }
}

function inputType(field: WebsiteFormField): string {
  switch (field.fieldType) {
    case 'email':
      return 'email'
    case 'phone':
      return 'tel'
    case 'number':
      return 'number'
    case 'date':
      return 'date'
    default:
      return 'text'
  }
}

function autocomplete(field: WebsiteFormField): string | undefined {
  switch (field.leadMapping) {
    case 'name':
      return 'name'
    case 'email':
      return 'email'
    case 'phone':
      return 'tel'
    case 'company':
      return 'organization'
    default:
      return undefined
  }
}

function checked(field: WebsiteFormField, option: string): boolean {
  const value = answers[field.key]
  return Array.isArray(value) ? value.includes(option) : value === option
}

function stringValue(field: WebsiteFormField): string {
  const value = answers[field.key]
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return ''
}

function setString(field: WebsiteFormField, value: string): void {
  answers[field.key] = value
}

function booleanValue(field: WebsiteFormField): boolean {
  return Boolean(answers[field.key])
}

function setBoolean(field: WebsiteFormField, value: boolean): void {
  answers[field.key] = value
}

function stringArray(field: WebsiteFormField): string[] {
  const value = answers[field.key]
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []
}

function toggleString(field: WebsiteFormField, option: string, checked: boolean): void {
  const current = new Set(stringArray(field))
  if (checked) current.add(option)
  else current.delete(option)
  answers[field.key] = Array.from(current)
}

function fieldError(field: WebsiteFormField): string | undefined {
  return effectiveCapture.value.errors?.[field.key]
}

function submit(): void {
  if (effectiveCapture.value.submitting) return

  emit('submit', {
    formId: props.formId,
    answers: { ...answers, website: honeypot.value },
    website: honeypot.value,
  })
}

watch(
  () => effectiveCapture.value.succeeded,
  (succeeded) => {
    if (!succeeded) return

    for (const key of Object.keys(answers)) {
      answers[key] = ''
    }
    honeypot.value = ''
  },
)
</script>

<template>
  <form v-if="form" class="site:relative site:grid site:gap-5" novalidate @submit.prevent="submit">
    <div class="site:absolute site:left-[-10000px] site:top-auto site:h-px site:w-px site:overflow-hidden" aria-hidden="true">
      <label>Website<input v-model="honeypot" name="website" tabindex="-1" autocomplete="off" /></label>
    </div>

    <label
      v-for="field in form.fields"
      :key="field.key"
      class="site:grid site:gap-2 site:text-sm site:font-semibold"
      :class="field.fieldType === 'long_text' ? 'site:col-span-full' : undefined"
    >
      <span>
        {{ field.label }}
        <span v-if="!field.required" class="site:font-normal site:text-(--site-color-muted-text)">(optional)</span>
      </span>

      <template v-if="field.fieldType === 'long_text'">
        <textarea
          :model-value="stringValue(field)"
          :name="field.key"
          :placeholder="field.placeholder ?? undefined"
          :required="field.required"
          rows="4"
          class="site:w-full site:resize-y site:border site:border-(--site-color-border) site:bg-(--site-color-background) site:px-4 site:py-3 site:text-base site:font-normal site:leading-7 site:text-(--site-color-text) site:outline-none site:transition site:focus:border-(--site-color-primary) site:focus:ring-3 site:focus:ring-(--site-color-primary)/15"
          :style="{ borderRadius: 'var(--site-radius-sm)' }"
          @update:model-value="setString(field, $event)"
        />
      </template>

      <template v-else-if="field.fieldType === 'select'">
        <select
          :model-value="stringValue(field)"
          :name="field.key"
          :required="field.required"
          class="site:h-12 site:w-full site:border site:border-(--site-color-border) site:bg-(--site-color-background) site:px-4 site:pr-10 site:text-base site:font-normal site:text-(--site-color-text) site:outline-none site:transition site:focus:border-(--site-color-primary) site:focus:ring-3 site:focus:ring-(--site-color-primary)/15"
          :style="{ borderRadius: 'var(--site-radius-sm)' }"
          @update:model-value="setString(field, $event)"
        >
          <option value="" disabled>{{ field.placeholder || 'Select an option' }}</option>
          <option v-for="option in field.options ?? []" :key="option" :value="option">{{ option }}</option>
        </select>
      </template>

      <template v-else-if="field.fieldType === 'radio' || field.fieldType === 'checkbox_group'">
        <div class="site:flex site:flex-wrap site:gap-2">
          <label v-for="option in field.options ?? []" :key="option" class="site:cursor-pointer">
            <input
              :checked="field.fieldType === 'radio' ? stringValue(field) === option : checked(field, option)"
              :type="field.fieldType === 'radio' ? 'radio' : 'checkbox'"
              :name="field.key"
              :value="option"
              :required="field.required && field.fieldType === 'radio'"
              class="site:sr-only"
              @change="field.fieldType === 'radio' ? setString(field, option) : toggleString(field, option, ($event.target as HTMLInputElement).checked)"
            />
            <span
              class="site:inline-flex site:min-h-10 site:items-center site:border site:px-4 site:text-sm site:font-semibold site:transition"
              :class="checked(field, option) ? 'site:border-(--site-color-primary) site:bg-(--site-color-primary)/10 site:text-(--site-color-primary)' : 'site:border-(--site-color-border) site:bg-(--site-color-background) site:text-(--site-color-muted-text)'"
              :style="{ borderRadius: 'var(--site-radius-sm)' }"
            >{{ option }}</span>
          </label>
        </div>
      </template>

      <template v-else-if="field.fieldType === 'checkbox'">
        <label class="site:flex site:cursor-pointer site:items-center site:gap-3">
          <input
            :checked="booleanValue(field)"
            :name="field.key"
            type="checkbox"
            class="site:size-5 site:accent-(--site-color-primary)"
            @change="setBoolean(field, ($event.target as HTMLInputElement).checked)"
          />
          <span class="site:font-normal site:text-(--site-color-text)">{{ field.placeholder || 'Yes' }}</span>
        </label>
      </template>

      <template v-else>
        <input
          :model-value="stringValue(field)"
          :name="field.key"
          :type="inputType(field)"
          :placeholder="field.placeholder ?? undefined"
          :required="field.required"
          :autocomplete="autocomplete(field)"
          class="site:min-h-12 site:w-full site:border site:border-(--site-color-border) site:bg-(--site-color-background) site:px-4 site:text-base site:font-normal site:text-(--site-color-text) site:outline-none site:transition site:focus:border-(--site-color-primary) site:focus:ring-3 site:focus:ring-(--site-color-primary)/15"
          :style="{ borderRadius: 'var(--site-radius-sm)' }"
          @update:model-value="setString(field, $event)"
        />
      </template>

      <span v-if="field.helpText" class="site:text-xs site:font-normal site:leading-5 site:text-(--site-color-muted-text)">{{ field.helpText }}</span>
      <span v-if="fieldError(field)" class="site:text-xs site:font-medium site:text-red-600">{{ fieldError(field) }}</span>
    </label>

    <div class="site:flex site:flex-wrap site:items-center site:gap-4">
      <button type="submit" class="site-site-button" :disabled="effectiveCapture.submitting">
        {{ effectiveCapture.submitting ? 'Sending...' : submitLabel }}
      </button>
      <p v-if="effectiveCapture.message" class="site:text-sm site:font-medium" :class="effectiveCapture.succeeded ? 'site:text-(--site-color-primary)' : 'site:text-red-600'" role="status" aria-live="polite">{{ effectiveCapture.message }}</p>
    </div>
  </form>

  <div v-else class="site:py-6 site:text-center">
    <p class="site-heading site:text-lg site:font-bold">This form is not ready yet.</p>
    <p class="site:mt-2 site:text-sm site:text-(--site-color-muted-text)">Choose a saved form in the website editor.</p>
  </div>
</template>
